import { outerMargin } from "@/App";
import { useElementSpacing } from "@/hooks/useElementSpacing";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import {
  Accessibility,
  AlertCircle,
  BarChart2,
  ChevronLeft,
  ChevronRight,
  HelpCircle as CircleHelp,
  Cloud,
  Headphones,
  Heart,
  Laptop,
  Music,
  Play,
  Pointer,
  Sparkles,
  Vibrate,
  Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import { useHapticEngine } from "../hooks/useHapticsEngine";
import { usePlayerStore } from "../stores/playerStore";
import { sidebarWidth } from "./Sidebar";
import { TrackList } from "./TrackList";
import { SliderContext, useSlider } from "./sliderContext";
import { Alert, AlertDescription } from "./ui/alert";

// Update shared styles for better centering
const SHARED_SECTION_STYLES = {
  card: "backdrop-blur-xl bg-zinc-900/50 border border-white/10 rounded-[--outer-margin] p-6 md:p-8 relative overflow-hidden h-[80vh] md:h-[60vh]  flex flex-col justify-start md:justify-center ",
  container: "w-full max-w-7xl mx-auto px-4 md:px-6 h-screen flex items-center justify-center",
  innerWrapper: "w-full h-[96%] md:h-[60%]",
};

const FeatureCard = ({ icon: Icon, title, description, delay = 0, gradient, ...motionProps }: any) => (
  <motion.div
    {...motionProps}
    className={`${SHARED_SECTION_STYLES.card} !h-fit group hover:border-white/20 transition-all duration-500 min-h-[238px] ${gradient}`}
  >
    <div
      className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl -z-10"
      style={{ background: gradient.replace("10", "20") }}
    />
    <div className="flex flex-col md:flex-row flex-wrap items-center gap-4 mb-4">
      <div className="p-4 bg-white/10 rounded-xl group-hover:scale-110 transition-transform duration-500">
        <Icon className="w-8 h-8" />
      </div>
      <h3 className="text-xl font-bold">{title}</h3>
    </div>
    <p className="text-zinc-400 text-lg leading-relaxed">{description}</p>
  </motion.div>
);

const SwipeHint = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: [0, 1, 0] }}
    transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
    className="fixed bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 text-white/50 md:hidden"
  >
    <Pointer className="w-6 h-6" />
    <span className="text-sm">Swipe to navigate</span>
  </motion.div>
);

const NavigationButton = ({ direction, onClick }: { direction: "left" | "right"; onClick: () => void }) => (
  <motion.button
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    onClick={onClick}
    className="flex items-center justify-center w-12 h-12 rounded-full bg-white/10 backdrop-blur-lg hover:bg-white/20 transition-colors"
  >
    {direction === "left" ? <ChevronLeft className="w-6 h-6" /> : <ChevronRight className="w-6 h-6" />}
  </motion.button>
);

// Update the carousel container sections mapping
export const HomeEmptyState = () => {
  // calc(100% - (16rem + 2rem + 21px))
  const width = `calc(100% - (${sidebarWidth} + (${outerMargin} * 3)))`; //"83.5%";
  const [currentPage, setCurrentPage] = useState(0);
  const sliderRef = useRef<Slider>(null);
  const { currentTrack } = usePlayerStore();

  const sections = [
    { id: "hero", component: HeroSection },
    { id: "demo", component: DemoSection },
    { id: "features", component: FeaturesSection },
    { id: "about", component: AboutSection },
    { id: "getStarted", component: GetStartedSection },
  ];

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    swipe: true,
    beforeChange: (old: number, next: number) => setCurrentPage(next),
  };

  const navigateSection = (direction: "next" | "prev") => {
    if (direction === "next") {
      sliderRef.current?.slickNext();
    } else {
      sliderRef.current?.slickPrev();
    }
  };

  useElementSpacing({
    selector: "indicators",
    property: "bottom",
    condition: !!currentTrack,
    spacing: "144px",
    mobileSpacing: "200px",
  });

  return (
    <SliderContext.Provider value={{ sliderRef }}>
      <div className="h-full w-full overflow-hidden relative bg-black">
        {/* Animated Background */}
        <div className="fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 to-blue-500/20 animate-gradient-slow" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(0,0,0,0)_0%,_rgba(0,0,0,0.8)_50%,_rgba(0,0,0,1)_100%)]" />
        </div>

        {/* Desktop Navigation Buttons - Fixed visibility */}
        <div
          style={{ "--hesWidth": width } as React.CSSProperties}
          className={`fixed bottom-32 hidden md:flex justify-between items-center w-full md:w-[--hesWidth] px-8 z-50`}
        >
          <NavigationButton direction="left" onClick={() => navigateSection("prev")} />
          <NavigationButton direction="right" onClick={() => navigateSection("next")} />
        </div>

        {/* Carousel Navigation Indicators - Updated for pill shape */}
        <div
          style={{ "--hesWidth": width } as React.CSSProperties}
          className={`fixed bottom-[10vh] md:bottom-36 mx-auto z-[49] flex flex-row justify-center gap-3 w-full md:w-[--hesWidth] indicators`}
        >
          {sections.map((section, index) => (
            <motion.button
              key={section.id}
              className={`h-3 rounded-full transition-all duration-300 ${
                currentPage === index ? "bg-yellow-500 w-8" : "bg-zinc-600 w-3"
              }`}
              onClick={() => sliderRef.current?.slickGoTo(index)}
              whileHover={{ scale: 1.2 }}
              layout
              aria-label={`Go to ${section.id}`}
            />
          ))}
        </div>

        {/* Carousel Container - Modified for better scrolling */}
        <Slider ref={sliderRef} {...settings} className="h-full">
          {sections.map(({ id, component: Component }) => (
            <div key={id} className="h-full">
              <div className="min-w-full h-full flex items-center justify-center overflow-y-auto">
                <Component />
              </div>
            </div>
          ))}
        </Slider>

        {/* Swipe Hint */}
        <SwipeHint />
      </div>
    </SliderContext.Provider>
  );
};

// Section Components
const HeroSection = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const { sliderRef } = useSlider();

  return (
    <div className={SHARED_SECTION_STYLES.container}>
      <div className={SHARED_SECTION_STYLES.innerWrapper}>
        <motion.div style={{ y }} className={SHARED_SECTION_STYLES.card}>
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div
              animate={{
                scale: [2, 4.2, 2],
                rotate: [0, 180, 360],
              }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="hidden w-full h-full bg-gradient-to-r from-yellow-500/20 to-blue-500/20 blur-3xl"
            />
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative mb-4 md:mb-10"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-500 to-blue-500 rounded-full animate-pulse blur-3xl opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-pulse blur-3xl opacity-20 animation-delay-1000" />
            <Vibrate className="w-24 h-24 md:w-28 md:h-28 text-yellow-500 relative animate-float mx-auto" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center space-y-6 md:space-y-10"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-white via-green-300 to-blue-300 bg-clip-text text-transparent">
              Welcome to Haptics Music Player
            </h1>
            <p className="text-lg md:text-2xl text-zinc-400 leading-relaxed max-w-2xl mx-auto">
              Experience music in a whole new way with haptic feedback that brings your favorite tracks to life.
            </p>
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-center justify-center !mb-12">
              <Link
                to="/library"
                className="flex items-center gap-3 px-8 py-4 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition-all hover:scale-105"
              >
                <Music className="w-6 h-6" />
                Get Started
              </Link>
              <button
                onClick={() => sliderRef?.current?.slickGoTo(1)}
                className="flex items-center gap-3 px-8 py-4 bg-zinc-800 text-white rounded-full hover:bg-zinc-700 transition-all hover:scale-105 cursor-pointer"
              >
                <Play className="w-6 h-6" />
                Try Demo
              </button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

const DemoSection = () => {
  const [demoTracks, setDemoTracks] = useState([]);
  const [showVibrationAlert, setShowVibrationAlert] = useState(false);
  const { setCurrentTrack, setIsPlaying } = usePlayerStore();
  const haptics = useHapticEngine(null);

  useEffect(() => {
    if (haptics.settings.enabled) {
      setShowVibrationAlert(true);
      const timer = setTimeout(() => setShowVibrationAlert(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [haptics.settings.enabled]);

  useEffect(() => {
    fetch("/demo/tracks.json")
      .then((res) => res.json())
      .then((data) => setDemoTracks(data.tracks));
  }, []);

  const handlePlay = (track) => {
    setCurrentTrack(track);
    setIsPlaying(true);
  };

  return (
    <div className={SHARED_SECTION_STYLES.container}>
      <div className={SHARED_SECTION_STYLES.innerWrapper}>
        <motion.div className={SHARED_SECTION_STYLES.card + " !justify-start"}>
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div
              animate={{
                x: [-100, 100, -100],
                y: [-100, 100, -100],
              }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              className="w-full h-full bg-gradient-to-br from-purple-500/20 via-pink-500/20 to-red-500/20 blur-3xl"
            />
          </div>
          <div className="flex flex-col gap-6 mb-8">
            <div className="flex items-center justify-start gap-4">
              <motion.div animate={{ y: [0, -5, 0] }} transition={{ duration: 2, repeat: Infinity }}>
                <Play className="w-8 h-8 text-yellow-500" />
              </motion.div>
              <h2 className="text-2xl md:text-3xl font-bold text-center md:text-left">Try it Now</h2>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 backdrop-blur-lg bg-white/5 border border-white/10 rounded-lg"
            >
              <div className="flex items-center gap-4 justify-between">
                <div className="flex items-center gap-3">
                  <Vibrate className={`w-6 h-6 ${haptics.settings.enabled ? "text-yellow-500" : "text-zinc-500"}`} />
                  <div className="flex flex-col">
                    <span className="font-medium">Haptic Feedback</span>
                    <span className="text-sm text-zinc-400">
                      {haptics.isSupported
                        ? `Status: ${haptics.settings.enabled ? "Enabled" : "Disabled"}`
                        : "Not supported on this device"}
                    </span>
                  </div>
                </div>

                {haptics.isSupported && (
                  <button
                    onClick={haptics.toggleEnabled}
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                    style={{
                      backgroundColor: haptics.settings.enabled ? "#EAB308" : "#27272A",
                    }}
                  >
                    <motion.span
                      layout
                      className="inline-block h-4 w-4 transform rounded-full bg-white"
                      animate={{
                        translateX: haptics.settings.enabled ? "24px" : "4px",
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 30,
                      }}
                    />
                  </button>
                )}
              </div>
            </motion.div>
          </div>

          <TrackList tracks={demoTracks} onPlay={handlePlay} showHeader={false} />

          <AnimatePresence>
            {showVibrationAlert && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mt-4"
              >
                <Alert variant="default" className="bg-yellow-500/10 border-yellow-500/50">
                  <AlertCircle className="w-4 h-4 text-yellow-500" />
                  <AlertDescription className="text-yellow-500 flex items-center justify-between">
                    <span>You should have felt a vibration pattern. If not, check your device settings.</span>
                    <button
                      onClick={() => setShowVibrationAlert(false)}
                      className="text-yellow-500/80 hover:text-yellow-500"
                    >
                      Dismiss
                    </button>
                  </AlertDescription>
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

const FeaturesSection = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isAutoHovering, setIsAutoHovering] = useState(true);
  const autoHoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userHoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const featureCards = [
    {
      icon: Vibrate,
      title: "Haptic Feedback",
      description: "Enhances your listening experience with precise haptic feedback.",
      gradient: "bg-gradient-to-br from-yellow-500/10 to-blue-500/10",
    },
    {
      icon: Headphones,
      title: "Universal Player",
      description: "Play music from any source - local files or Spotify - all in one beautiful interface.",
      gradient: "bg-gradient-to-br from-purple-500/10 to-pink-500/10",
    },
    {
      icon: Heart,
      title: "Smart Collections",
      description: "Organize your music with intelligent playlists and categories.",
      gradient: "bg-gradient-to-br from-orange-500/10 to-red-500/10",
    },
    {
      icon: BarChart2,
      title: "Rich Statistics",
      description: "Gain insights into your listening habits with detailed analytics and visualizations.",
      gradient: "bg-gradient-to-br from-blue-500/10 to-indigo-500/10",
    },
    {
      icon: Accessibility,
      title: "Accessibility First",
      description: "Designed for everyone with full keyboard navigation and screen reader support.",
      gradient: "bg-gradient-to-br from-yellow-500/10 to-orange-500/10",
    },
    {
      icon: Cloud,
      title: "Cross-Platform",
      description: "Access your music anywhere with seamless cloud sync and offline support.",
      gradient: "bg-gradient-to-br from-teal-500/10 to-yellow-500/10",
    },
  ];

  const startAutoHover = () => {
    if (!isAutoHovering) return;

    autoHoverTimeoutRef.current = setTimeout(() => {
      const randomIndex = Math.floor(Math.random() * featureCards.length);
      setHoveredIndex(randomIndex);
      startAutoHover(); // Schedule next hover
    }, 3000);
  };

  const stopAutoHover = () => {
    if (autoHoverTimeoutRef.current) {
      clearTimeout(autoHoverTimeoutRef.current);
    }
  };

  const handleMouseEnter = (index: number) => {
    stopAutoHover();
    setHoveredIndex(index);
    setIsAutoHovering(false);

    // Resume auto-hover after 3 seconds of no interaction
    userHoverTimeoutRef.current = setTimeout(() => {
      setIsAutoHovering(true);
      startAutoHover();
    }, 3000);
  };

  const handleMouseLeave = () => {
    if (userHoverTimeoutRef.current) {
      clearTimeout(userHoverTimeoutRef.current);
    }
    setHoveredIndex(null);

    if (isAutoHovering) {
      startAutoHover();
    }
  };

  useEffect(() => {
    startAutoHover();

    return () => {
      stopAutoHover();
      if (userHoverTimeoutRef.current) {
        clearTimeout(userHoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={SHARED_SECTION_STYLES.container}>
      <div className={SHARED_SECTION_STYLES.innerWrapper}>
        <motion.div className={`${SHARED_SECTION_STYLES.card} !justify-start overflow-y-auto`}>
          <div className="absolute inset-0 h-[250%] md:h-[170%] lg:h-full overflow-hidden">
            <motion.div
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
              className="w-full h-full bg-gradient-to-tr from-green-500/20 via-blue-500/20 to-teal-500/20 blur-3xl"
            />
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row items-center justify-center gap-4 mb-8 md:mb-16 top-0 py-4 z-10"
          >
            <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity }}>
              <Sparkles className="w-8 h-8 text-yellow-500" />
            </motion.div>
            <h2 className="text-3xl md:text-4xl font-bold text-center">Features that make a difference</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 w-full">
            {featureCards.map((card, index) => (
              <FeatureCard
                key={card.title}
                icon={card.icon}
                title={card.title}
                description={card.description}
                delay={0.1 * (index + 1)}
                gradient={card.gradient}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "5px" }}
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
                className={hoveredIndex === index ? "group hover:border-white/20" : ""}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const AboutSection = () => (
  <div className={SHARED_SECTION_STYLES.container}>
    <div className={SHARED_SECTION_STYLES.innerWrapper}>
      <motion.div className={SHARED_SECTION_STYLES.card + "overflow-y-auto"}>
        <div className="absolute inset-0 h-[130%] md:h-full overflow-hidden">
          <motion.div
            animate={{
              rotate: [0, 360],
              scale: [2, 2.2, 1.8, 2],
            }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="w-full h-full bg-gradient-to-bl from-red-500/20 via-zinc-500/20 to-yellow-500/20 blur-3xl"
          />
        </div>
        <div className="flex flex-col md:flex-row items-center gap-4 mb-6 md:mb-8">
          <motion.div
            animate={{ opacity: [0.2, 1, 0.2], scale: [0.95, 1, 0.95] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <CircleHelp className="w-12 h-12 text-yellow-500 translate-y-0.5" />
          </motion.div>
          <h2 className="text-3xl md:text-4xl font-bold relative">Why Haptics Music Player?</h2>
        </div>

        <div className="prose prose-invert prose-lg mx-auto relative">
          <p className="text-base md:text-xl text-zinc-400 leading-relaxed mb-6 md:mb-8">
            As a music enthusiast and accessibility advocate, I created Haptics Music Player to bridge the gap between
            audio and tactile feedback. The inspiration came from seeing how rhythm can be experienced beyond just
            sound, making music more inclusive and immersive for everyone.
          </p>
          <p className="text-base md:text-xl text-zinc-400 leading-relaxed">
            What started as an experiment in haptic feedback has evolved into a full-featured music player that puts
            accessibility and user experience first. Whether you're a casual listener or an audiophile, Haptics Music
            Player brings a new dimension to your music.
          </p>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-start gap-12 mt-12">
          <div className="flex items-center gap-3">
            <Laptop className="w-6 h-6 text-yellow-500" />
            <span className="text-lg text-zinc-400">Local First</span>
          </div>
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-yellow-500" />
            <span className="text-lg text-zinc-400">Lightning Fast</span>
          </div>
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-yellow-500" />
            <span className="text-lg text-zinc-400">Regular Updates</span>
          </div>
        </div>
      </motion.div>
    </div>
  </div>
);

const GetStartedSection = () => (
  <div className={SHARED_SECTION_STYLES.container}>
    <div className={SHARED_SECTION_STYLES.innerWrapper}>
      <motion.div className={SHARED_SECTION_STYLES.card}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              y: [-100, 100, -100],
              scale: [1, 1.1, 1],
            }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="w-full h-full bg-gradient-to-tl from-indigo-500/20 via-purple-500/20 to-blue-500/20 blur-3xl"
          />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="w-full text-center space-y-8"
        >
          <div className="min-h-[60vh] flex flex-col items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative mb-8"
            >
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Music className="w-24 h-24 text-yellow-500 relative" />
              </motion.div>
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-500 to-blue-500 rounded-full animate-spin-slow blur-xl opacity-20" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-center max-w-md mb-8"
            >
              <h2 className="text-3xl font-bold mb-4">Ready to feel the music?</h2>
              <p className="text-zinc-400">
                Upload your local music files or connect with Spotify to begin building your personalized music
                experience.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <Link
                to="/local"
                className="flex items-center gap-3 px-6 py-4 bg-purple-400/10 rounded-lg hover:bg-purple-400/20 transition group"
              >
                <div className="p-3 bg-purple-400/20 rounded-full group-hover:bg-purple-400/30">
                  <Music className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">Upload Music</h3>
                  <p className="text-sm text-zinc-400">Add your local files</p>
                </div>
              </Link>

              <Link
                to="/search"
                className="flex items-center gap-3 px-6 py-4 bg-yellow-500 rounded-lg hover:bg-yellow-600 transition group"
              >
                <div className="p-3 bg-yellow-600 rounded-full group-hover:bg-yellow-700">
                  <Play className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">Discover Music</h3>
                  <p className="text-sm text-green-100">Find new tracks</p>
                </div>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mt-12 flex items-center gap-2 text-zinc-400"
            >
              <Sparkles className="w-4 h-4" />
              <span className="text-sm">Pro tip: Connect Spotify for more features</span>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </div>
);
